import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Modal, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from '@mui/icons-material/Close';
import {
  addBillingPrice,
  getBillingCharges,
  getBillingPrice,
} from "redux/actions/BillingAction";
import { getReservationDetails } from "redux/actions/reservationAction";
import { tripDutyTypeEnum } from "utils/enumObj";
import { formatCurrency, formatNumber, getDateFormat } from "utils/utils";
import useModal from "hooks/useModal";
import Loader from "components/loader/Loader";
import CommonButton from "components/commonButton/CommonButton";
import CommonTable from "components/commonTable/CommonTable";
import { CustomInvoiceTextfield } from "./CustomInvoiceTextfield";

const GenerateInvoiceModal = ({
  open,
  setOpen,
  tripDetails,
  invoiceType,
  pageType,
  startDate,
  endDate,
  paramsDetails
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [expandMore, setExpandMore] = useState([false, false, false]);
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [previewImage, setPreviewImage] = useState("");
  const { open: previewModal, toggle: setPreviewModal } = useModal();

  const dutyType =
    tripDetails?.dutyTypeName?.toLowerCase() ||
    tripDetails?.tripType?.toLowerCase();

  const tripID = tripDetails?.tripUuid || tripDetails?.id;

  const bookingDetails = useSelector(
    (state) => state.getReservationDetails?.reservationDetails?.data
  );

  const companyName = tripDetails?.companyName || bookingDetails?.companyName;

  useEffect(() => {
    if (open) {
      dispatch(getBillingPrice(tripID));
    }
  }, [open]);

  useEffect(() => {
    if (invoiceType && open) {
      dispatch(getReservationDetails(tripID));
    }
  }, [invoiceType, open]);

  const tableData = {
    header: [
      {
        label: "Duty ID",
        key: "dutyId",
      },
      {
        label: "Date",
        key: "scheduledDate",
      },
      {
        label: "Passenger",
        key: "passenger",
      },
      {
        label: "Vehicle",
        key: "vehicle",
      },
      {
        label: "Duty Type",
        key: "dutyType",
      },
    ],
    body: [
      {
        dutyId: tripDetails?.tripId || "NA",
        scheduledDate: getDateFormat(tripDetails?.scheduledTime),
        passenger: tripDetails?.customerName || "NA",
        vehicle: tripDetails?.carType || "NA",
        dutyType: tripDetails?.dutyTypeName || tripDetails?.tripType || "NA",
      },
    ],
  };

  const priceDetails = useSelector(
    (state) => state.getBillingPriceDetails.bilingPriceDetails?.data
  );

  const priceDetailsStatus = useSelector(
    (state) => state.getBillingPriceDetails.bilingPriceDetails
  );

  const priceDetailsLoader = useSelector(
    (state) => state.getBillingPriceDetails?.loading
  );

  //Extra charges response
  const extraChargesLoader = useSelector(
    (state) => state?.getBillingChargesCalculations?.loading
  );

  const extraCharges = useSelector(
    (state) => state?.getBillingChargesCalculations?.chargesCalculations?.data
  );

  useEffect(() => {
    if (priceDetailsStatus?.success) {
      const tempPriceDetails = {
        airportTripDistance: priceDetails?.tripPrice?.distance || 0,
        airportTripCharges: priceDetails?.tripPrice?.tripPrice || 0,
        packageDays: priceDetails?.tripPrice?.time || 0,
        packageKm: priceDetails?.tripPrice?.distance || 0,
        packageCharges: priceDetails?.tripPrice?.tripPrice || 0,
        slabPrice:priceDetails?.tripPrice?.slabPrice || 0,
        extraDays: priceDetails?.tripPrice?.extraTime || 0,
        extraKm: priceDetails?.tripPrice?.extraKm || 0,
        extraCharges: priceDetails?.tripPrice?.extraCharges || 0,
        tollAmount: priceDetails?.toll?.amount || 0,
        parkingAmount: priceDetails?.parking?.amount || 0,
        interstateTax: priceDetails?.interstateTax?.amount || 0,
        driverAllowance: priceDetails?.driverAllowance || 0,
        nightAllowance: priceDetails?.nightAllowance || 0,
        nightStart: priceDetails?.nightStart || 0,
        nightEnd: priceDetails?.nightEnd || 0,
      };
      setInvoiceDetails(tempPriceDetails);

      const payload = {
        tripId: tripID,
        extraTime: priceDetails?.tripPrice?.extraTime || 0,
        extraDistance: priceDetails?.tripPrice?.extraKm || 0,
      };

      if (
        tripID &&
        open &&
        (dutyType === "outstation" || dutyType === "rental")
      ) {
        dispatch(getBillingCharges(payload));
      }
    }
  }, [priceDetails]);

  const handleClose = () => {
    setOpen();
  };

  const handleApply = () => {
    handleClose();

    const payloadData = {
      extraDistance: invoiceDetails?.extraKm || 0,
      //multiplying days into 24 hour format due to backend accepting in hours format
      extraTime: tripDetails?.type === tripDutyTypeEnum?.roundTrip ? invoiceDetails?.extraDays * 24 : invoiceDetails?.extraDays || 0,
      toll: invoiceDetails?.tollAmount || 0,
      parking: invoiceDetails?.parkingAmount || 0,
      interstateTax: invoiceDetails?.interstateTax || 0,
      driverAllowance: invoiceDetails?.driverAllowance || 0,
      nightAllowance: invoiceDetails?.nightAllowance || 0,
    };

    const handleNavigate = () => {
      navigate(
        `/operation/billing/duty-slip?tripId=${tripID}&companyId=${
          bookingDetails?.companyId
        }&companyName=${bookingDetails?.companyName}&cityId=${
          bookingDetails?.city
        }&cityName=${
          bookingDetails?.location
        }&status=${"pendingApproval"}&pageType=${pageType}&startDate=${startDate}&endDate=${endDate}&page=${paramsDetails?.page}&rowPerPage=${paramsDetails?.rowPerPage}&search=${paramsDetails?.search}`
      );
    };

    dispatch(addBillingPrice(payloadData, tripID, handleNavigate));
  };

  const onRestFilter = () => {
    handleClose();
  };

  const handleCollapse = (id) => {
    let newArr = [...expandMore];

    if (newArr[id]) {
      newArr[id] = false;
    } else {
      newArr[id] = true;
    }
    setExpandMore(newArr);
  };

  const expandArrow = (index) => {
    return (
      <Box role="presentation" className="justifyCenter" onClick={() => handleCollapse(index)}>
        {expandMore?.[index] ? (
          <KeyboardArrowUpIcon />
        ) : (
          <KeyboardArrowDownIcon />
        )}
      </Box>
    );
  };
  const calcTotal = () => {
    const tripType = tripDetails?.tripEnum || tripDetails?.type;
    let overallTotal =
      Number(invoiceDetails?.tollAmount) +
      Number(invoiceDetails?.parkingAmount) + Number(invoiceDetails?.interstateTax);
    if (tripType === tripDutyTypeEnum.airpotTransfer) {
      overallTotal += Number(invoiceDetails?.airportTripCharges);
    } else if (tripType === tripDutyTypeEnum.rental) {
      overallTotal +=
        Number(invoiceDetails?.slabPrice) + Number(extraCharges?.price);
    } else {
      overallTotal =
        overallTotal +
        Number(invoiceDetails?.slabPrice) +
        Number(extraCharges?.price) +
        Number(invoiceDetails?.driverAllowance) +
        Number(invoiceDetails?.nightAllowance);
    }

    return formatCurrency(overallTotal);
  };

  const handleExtraCalculationCharges = (name, value) => {
    const payload = {
      tripId: tripID,
      extraTime: name === "extraDays" ? value : invoiceDetails?.extraDays || 0,
      extraDistance: name === "extraKm" ? value : invoiceDetails?.extraKm || 0,
    };

    dispatch(getBillingCharges(payload));
  };

  const handlePreviewImage = (url) => {
    setPreviewModal();
    setPreviewImage(url);
  };

  return (
    <Box>
      <Modal
        open={open || false}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="customModal"
        role="modal"
      >
        <Box
          className="modalContent  generateInvoiceModalContent textLeft"
          sx={{ height: { xs: "100%", sm: "auto" } }}
        >
          {priceDetailsLoader ? (
            <Loader height={"80vh"} />
          ) : (
            <Box className="generateInvoiceContentWrapper">
              <Typography variant="head3" className="colorPrimary fw600 pb10">
                Review & Confirm
              </Typography>
              <Typography variant="body1" className="colorGrey fw400 pb10">
                Showing unbilled trip of selected booking for company&nbsp;
                {companyName}
              </Typography>

              <Box className="pt10 pb30 horizontalDashedBorder">
                <CommonTable data={tableData} dispatchTableMain={true} />
              </Box>

            {/* Only for outstation */}
            {(dutyType === "outstation" || dutyType === "rental") && (
              <Box>
                <Typography variant="body1" className="colorPrimary pt20 pl20">
                  Total km & Charges
                </Typography>
                <Grid container>
                  <Grid
                    container
                    item
                    xs={12}
                    className="height48"
                    alignItems="center"
                  >
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" className="pl20 colorGrey">
                        Details
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" className="colorGrey">
                        {dutyType === "outstation" ? 'Days' : 'hr'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" className="colorGrey">
                        km
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" className="colorGrey">
                        Charges
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    item
                    xs={12}
                    className="height48 mb10 bgLightPrimary"
                    alignItems="center"
                  >
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" className="pl20">
                        Package
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2">
                        {invoiceDetails?.packageDays}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2">
                        {formatNumber(invoiceDetails?.packageKm)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2">
                        ₹&nbsp;
                        {formatCurrency(invoiceDetails?.slabPrice)}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    item
                    xs={12}
                    className="height48 mb10 bgLightPrimary"
                    alignItems="center"
                  >
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" className="pl20">
                        Extra km
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <CustomInvoiceTextfield
                        adornmentEnd
                        adormentEndType={"edit"}
                        name={"extraDays"}
                        customClass={"numberField"}
                        invoiceDetails={invoiceDetails}
                        setInvoiceDetails={setInvoiceDetails}
                        handleExtraCalculationCharges={
                          handleExtraCalculationCharges
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <CustomInvoiceTextfield
                        adornmentEnd
                        adormentEndType={"edit"}
                        name={"extraKm"}
                        customClass={"numberField"}
                        invoiceDetails={invoiceDetails}
                        setInvoiceDetails={setInvoiceDetails}
                        handleExtraCalculationCharges={
                          handleExtraCalculationCharges
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      {!extraChargesLoader ? (
                        <Typography variant="body2">
                          ₹&nbsp;
                          {formatCurrency(extraCharges?.price)}
                        </Typography>
                      ) : (
                        "......"
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Only for Airport Transfer */}
            {dutyType === "airport transfer" && (
              <Box>
                <Typography variant="body1" className="colorPrimary pt20 pl20">
                  Total km & Charges
                </Typography>
                <Grid container>
                  <Grid
                    container
                    item
                    xs={12}
                    className="height48"
                    alignItems="center"
                  >
                    <Grid item xs={12} md={8}>
                      <Typography variant="body2" className="pl20 colorGrey">
                        km
                      </Typography>
                    </Grid>
                    <Grid
                      container
                      item
                      xs={12}
                      className="height48 mb10 bgLightPrimary"
                      alignItems="center"
                    >
                      <Grid item xs={12} md={8}>
                        <Typography variant="body2" className="pl20">
                          {invoiceDetails?.airportTripDistance}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography variant="body2">
                          ₹ {invoiceDetails?.airportTripCharges}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  </Grid>
                </Box>
              )}

              <Typography variant="body1" className="colorPrimary pt30 pl20">
                Toll & Parking Charges
              </Typography>
              <Grid>
                <Grid
                  container
                  item
                  xs={12}
                  className="height48"
                  alignItems="center"
                >
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" className="pl20 colorGrey">
                      Receipt Type
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" className="colorGrey">
                      Date
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" className="colorGrey">
                      Charges
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  className="height48 bgLightPrimary mb10"
                  alignItems="center"
                >
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" className="pl20">
                      Toll
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      {getDateFormat(tripDetails?.scheduledTime)}
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    className="pr20"
                    alignItems={"center"}
                    item
                    xs={12}
                    md={4}
                  >
                    <CustomInvoiceTextfield
                      adornmentStart
                      adornmentEnd
                      adormentStartType={"rupee"}
                      adormentEndType={"edit"}
                      name={"tollAmount"}
                      customClass={"numberField"}
                      invoiceDetails={invoiceDetails}
                      setInvoiceDetails={setInvoiceDetails}
                    />
                    {expandArrow(0)}
                  </Grid>
                </Grid>
                {expandMore?.[0] && (
                  <Grid
                    container
                    gap={3}
                    flexWrap={"nowrap"}
                    item
                    xs={12}
                    className="expandMoreImage p10 pb20"
                  >
                    {priceDetails?.toll?.receipts?.length ? (
                      priceDetails?.toll?.receipts?.map((imgData, index) => (
                        <Grid item xs={2} key={index}>
                          <img
                            onClick={() => handlePreviewImage(imgData?.url)}
                            src={imgData?.url}
                            className="tollParkingImg"
                            alt="Toll Images"
                          />
                        </Grid>
                      ))
                    ) : (
                      <Typography className="jusifyCenter pb10">
                        No Images
                      </Typography>
                    )}
                  </Grid>
                )}
                <Grid
                  container
                  item
                  xs={12}
                  className="height48 bgLightPrimary mb10"
                  alignItems="center"
                >
                  <Grid item xs={4}>
                    <Typography variant="body2" className="pl20">
                      Parking
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2">
                      {getDateFormat(tripDetails?.scheduledTime)}
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    item
                    xs={4}
                    className="pr20"
                    justifyContent={"space-between"}
                  >
                    <CustomInvoiceTextfield
                      adornmentStart
                      adornmentEnd
                      adormentStartType={"rupee"}
                      adormentEndType={"edit"}
                      name={"parkingAmount"}
                      customClass={"numberField"}
                      invoiceDetails={invoiceDetails}
                      setInvoiceDetails={setInvoiceDetails}
                    />
                    {expandArrow(1)}
                  </Grid>
                </Grid>
                {expandMore?.[1] && (
                  <Grid
                    container
                    gap={3}
                    flexWrap={"nowrap"}
                    item
                    xs={12}
                    className="expandMoreImage p10 pb20"
                  >
                    {priceDetails?.parking?.receipt?.length ? (
                      priceDetails?.parking?.receipt?.map((imgData, index) => (
                        <Grid item xs={2} key={index}>
                          <img
                            onClick={() => handlePreviewImage(imgData?.url)}
                            src={imgData?.url}
                            className="tollParkingImg"
                            alt="Parking Images"
                          />
                        </Grid>
                      ))
                    ) : (
                      <Typography className="pb10">No Images</Typography>
                    )}
                  </Grid>
                )}
              <Grid
                container
                item
                xs={12}
                className="height48 bgLightPrimary mb10"
                alignItems="center"
              >
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" className="pl20">
                    Interstate Tax
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2">
                    {getDateFormat(tripDetails?.scheduledTime)}
                  </Typography>
                </Grid>
                <Grid
                  container
                  justifyContent={"space-between"}
                  className="pr20"
                  alignItems={"center"}
                  item
                  xs={12}
                  md={4}
                >
                  <CustomInvoiceTextfield
                    adornmentStart
                    adornmentEnd
                    adormentStartType={"rupee"}
                    adormentEndType={"edit"}
                    name={"interstateTax"}
                    customClass={"numberField"}
                    invoiceDetails={invoiceDetails}
                    setInvoiceDetails={setInvoiceDetails}
                  />
                  {expandArrow(2)}
                </Grid>
              </Grid>
              {expandMore?.[2] && (
                <Grid
                  container
                  gap={3}
                  flexWrap={"nowrap"}
                  item
                  xs={12}
                  className="expandMoreImage p10 pb20"
                >
                  {priceDetails?.interstateTax?.receipt?.length ? (
                    priceDetails?.interstateTax?.receipt?.map(
                      (imgData, index) => (
                        <Grid item xs={2} key={index}>
                          <img
                            onClick={() => handlePreviewImage(imgData?.url)}
                            src={imgData?.url}
                            className="tollParkingImg"
                            alt="Toll Images"
                          />
                        </Grid>
                      )
                    )
                  ) : (
                    <Typography className="jusifyCenter pb10">
                      No Images
                    </Typography>
                  )}
                </Grid>
              )}
              </Grid>

              {dutyType === "outstation" && (
                <Box>
                  <Typography
                    variant="body1"
                    className="colorPrimary pt30 pl20"
                  >
                    Driver Allowance
                  </Typography>
                  <Grid>
                    <Grid
                      container
                      item
                      xs={12}
                      className="height48"
                      alignItems="center"
                    >
                      <Grid item xs={8}>
                        <Typography variant="body2" className="colorGrey pl20">
                          Allowance
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" className="colorGrey">
                          Charges
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      item
                      xs={12}
                      className="height48 bgLightPrimary"
                      alignItems="center"
                      justifyContent={"space-between"}
                    >
                      <Grid item xs={8}>
                        <Typography variant="body2" className="pl20">
                          Daily
                        </Typography>
                      </Grid>
                      <Grid item xs={4} className="pr20">
                        <CustomInvoiceTextfield
                          adornmentStart
                          adornmentEnd
                          adormentStartType={"rupee"}
                          adormentEndType={"edit"}
                          name={"driverAllowance"}
                          customClass={"numberField"}
                          invoiceDetails={invoiceDetails}
                          setInvoiceDetails={setInvoiceDetails}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Typography
                    variant="body1"
                    className="colorPrimary pt20 pl20"
                  >
                    Night Allowance
                  </Typography>
                  <Grid>
                    <Grid
                      container
                      item
                      xs={12}
                      className="height48"
                      alignItems="center"
                    >
                      <Grid item xs={8}>
                        <Typography variant="body2" className="colorGrey pl20">
                          Time
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" className="colorGrey">
                          Charges
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      item
                      xs={12}
                      className="height48 bgLightPrimary"
                      alignItems="center"
                      justifyContent={"space-between"}
                    >
                      <Grid item xs={8}>
                        <Typography variant="body2" className="pl20">
                          {invoiceDetails?.nightStart}&nbsp;to&nbsp;
                          {invoiceDetails?.nightEnd}
                        </Typography>
                      </Grid>
                      <Grid item xs={4} className="pr20">
                        <CustomInvoiceTextfield
                          adornmentStart
                          adornmentEnd
                          adormentStartType={"rupee"}
                          adormentEndType={"edit"}
                          name={"nightAllowance"}
                          customClass={"numberField"}
                          invoiceDetails={invoiceDetails}
                          setInvoiceDetails={setInvoiceDetails}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Grid
                container
                justifyContent={"flex-end"}
                className="mt20 ptb20 horizontalDashedBorder"
              >
                <Grid item xs={4}>
                  <Typography className="colorPrimary fw600 pr20 textRight">
                    Total Trip Charges
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography className="colorPrimary fw600">
                    ₹&nbsp;{calcTotal()}
                  </Typography>
                </Grid>
                <Grid item xs={2}></Grid>
              </Grid>

              <Grid container justifyContent={"flex-end"} className="mt30">
                <CommonButton
                  name="Cancel"
                  btnClass={"btnMedium btnTransparent"}
                  onClick={onRestFilter}
                />
                <CommonButton
                  name="Generate"
                  btnClass={"btnMedium"}
                  onClick={handleApply}
                />
              </Grid>
            </Box>
          )}
        </Box>
      </Modal>

      {previewModal && 
        <Modal
          open={previewModal || false}
          onClose={setPreviewModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          className="customModal"
        >
          <Box className="imagePreviewModal justifyCenter flexColumn">
            <Box className="width100 textRight" onClick={setPreviewModal}>
            <CloseIcon className="colorWhite" />
            </Box>
            <Box className="width100 textCenter">
              <img
                src={previewImage}
                alt="Preview_Image"
                className="imagePreviewModal br10"
              />
            </Box>
          </Box>
        </Modal>
      }
    </Box>
  );
};

export default GenerateInvoiceModal;
